"use client";

import { useMemo, useState, useRef, useEffect, useCallback, useId } from "react";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import { CaretDown, Check, MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";

// Diacritic-strip + lowercase. Lets "ha noi" match "Hà Nội" / "Hồ Chí Minh"
// against "ho chi" etc. when the Select is used for the province / city list.
// Pure helper inlined here so the generic Select doesn't pull in any
// province-specific modules — usable for any list with diacritic-aware labels.
function normaliseSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .trim();
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string;
  label: string;
  /**
   * Extra search synonyms (diacritic/space-insensitive). The option matches
   * the search query if the label OR any keyword matches. Used for province
   * aliases — e.g. "saigon" / "hcmc" → "Ho Chi Minh". Not shown in the UI.
   */
  keywords?: string[];
}

interface SelectProps {
  /** Options to display in the dropdown */
  options: SelectOption[];
  /** Current value (controlled) */
  value?: string;
  /** Initial value (uncontrolled) */
  defaultValue?: string;
  /** Called when value changes */
  onChange?: (value: string) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** HTML name attribute (for hidden input in native forms) */
  name?: string;
  /** HTML id attribute */
  id?: string;
  /** aria-label for the trigger button */
  "aria-label"?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field has a validation error */
  error?: boolean;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Visual variant */
  variant?: "light" | "dark";
  /** Additional className for the trigger button (e.g. custom pill styling) */
  triggerClassName?: string;
  /** Icon to render before the label inside the trigger button */
  triggerIcon?: React.ReactNode;
  /** REPLACES the dropdown list's whole class string (positioning included). */
  dropdownClassName?: string;
  /** Inside a modal (z-overlay): lift the list above the scrim. */
  inModal?: boolean;
  /**
   * When true, the open dropdown renders a search input at the top and
   * filters the option list by diacritic-stripped, case-insensitive
   * substring on the option label. Useful for long lists (e.g. the 34
   * Vietnamese provinces); leave off for short lists where the existing
   * first-letter typeahead is faster.
   */
  searchable?: boolean;
  /** Placeholder for the search input when `searchable` is true. */
  searchPlaceholder?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Select({
  options,
  value: controlledValue,
  defaultValue = "",
  onChange,
  placeholder = "Select…",
  name,
  id,
  "aria-label": ariaLabel,
  required,
  error,
  disabled,
  variant = "light",
  triggerClassName,
  triggerIcon,
  dropdownClassName,
  inModal = false,
  searchable = false,
  searchPlaceholder = "Type to filter…",
}: SelectProps) {
  const t = useTranslations("a11y");
  const uid = useId();
  const listboxId = `${uid}-listbox`;

  // Value state — controlled or uncontrolled
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? controlledValue : internalValue;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [typeAhead, setTypeAhead] = useState("");
  const typeAheadTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // `dropdownRef` covers the whole portalled popover (search input + listbox
  // in searchable mode, just the listbox otherwise). `listRef` is narrower —
  // the `<ul>` only — and feeds `scrollIntoView` on active item changes.
  // Splitting these two refs fixes the outside-click bug where clicking the
  // search input or its X button (both outside the `<ul>`) closed the
  // dropdown.
  const dropdownRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  // Search-filter state (active only when `searchable` is true).
  // Ranking rules: a label that *starts* with the query ranks highest, a
  // label whose *word* starts with the query next, a label that merely
  // *contains* the query as a substring next, and finally a keyword/alias
  // match. Within each tier, options keep their original order — stable sort.
  // So for query "ho": "Ho Chi Minh" beats "Cần Thơ" (substring "ho" inside
  // "tho") and "Hải Phòng" (inside "phong").
  //
  // Matching is also space/hyphen-insensitive via the `compact` form, so
  // "hanoi" matches "Ha Noi" and "hochiminh" matches "Ho Chi Minh" — typing
  // the run-together spelling is common and shouldn't dead-end at "No matches".
  const [query, setQuery] = useState("");
  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = normaliseSearch(query);
    if (!q) return options;
    const compact = (s: string) => s.replace(/[\s-]+/g, "");
    const qc = compact(q); // run-together query, e.g. "hanoi"
    const ranked: { option: SelectOption; score: number; idx: number }[] = [];
    options.forEach((option, idx) => {
      const n = normaliseSearch(option.label);
      const nc = compact(n);
      let score: number | null = null;
      if (n.startsWith(q) || (qc !== "" && nc.startsWith(qc))) score = 0;
      else if (n.split(/\s+/).some((w) => w.startsWith(q))) score = 1;
      else if (n.includes(q) || (qc !== "" && nc.includes(qc))) score = 2;
      else if (
        option.keywords?.some((k) => {
          const kn = normaliseSearch(k);
          return kn.includes(q) || (qc !== "" && compact(kn).includes(qc));
        })
      )
        score = 3;
      if (score === null) return; // no match (score 0 is valid — don't use falsy)
      ranked.push({ option, score, idx });
    });
    ranked.sort((a, b) => a.score - b.score || a.idx - b.idx);
    return ranked.map((r) => r.option);
  }, [options, query, searchable]);

  // Trigger position for the portalled dropdown. The dropdown is rendered
  // into <body> so it escapes ancestor `overflow: hidden` clipping (e.g. the
  // apply form layout's `h-screen overflow-hidden`). Position is computed in
  // viewport coords from the trigger's bounding rect, kept in sync on
  // scroll/resize.
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  const selectedOption = options.find((o) => o.value === currentValue);
  const activeOption = activeIndex >= 0 ? filteredOptions[activeIndex] : undefined;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const selectValue = useCallback(
    (val: string) => {
      if (!isControlled) setInternalValue(val);
      onChange?.(val);
      setOpen(false);
      setActiveIndex(-1);
      triggerRef.current?.focus();
    },
    [isControlled, onChange],
  );

  const openDropdown = useCallback(() => {
    if (disabled) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setTriggerRect(rect);
      // Determine flip direction
      const spaceBelow = window.innerHeight - rect.bottom;
      setFlipUp(spaceBelow < 240);
    }
    setOpen(true);
    // Set active index to current value within the *filtered* list. When
    // the user reopens an already-filtered dropdown, this keeps focus on
    // the still-visible selection rather than jumping to index 0.
    const idx = filteredOptions.findIndex((o) => o.value === currentValue);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [disabled, filteredOptions, currentValue]);

  // Close on outside click — must check both the trigger's container AND
  // the portalled listbox, since they're no longer DOM siblings.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setActiveIndex(-1);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Keep the portalled dropdown anchored to the trigger when the page scrolls
  // or the viewport resizes. `capture: true` on scroll catches events from
  // ANY ancestor (e.g. the apply form's internal scroll container), not just
  // window scroll.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        setTriggerRect(rect);
        setFlipUp(window.innerHeight - rect.bottom < 240);
      }
    };
    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    items[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // Reset search query when the dropdown closes so the next open starts
  // fresh. Focus the search input on open (one frame later, after the
  // portal has actually mounted).
  useEffect(() => {
    if (!searchable) return;
    if (open) {
      const id = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    setQuery("");
  }, [open, searchable]);

  // Keep `activeIndex` in range as the filter changes — typing a new char
  // often shrinks `filteredOptions`. Snap to 0 when out of bounds.
  useEffect(() => {
    if (!searchable || !open) return;
    if (activeIndex >= filteredOptions.length) {
      setActiveIndex(filteredOptions.length > 0 ? 0 : -1);
    }
  }, [filteredOptions.length, activeIndex, searchable, open]);

  // Keyboard handling
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (!open) {
        if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
          e.preventDefault();
          openDropdown();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
          break;
        case "Home":
          e.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          e.preventDefault();
          setActiveIndex(filteredOptions.length - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (activeIndex >= 0 && filteredOptions[activeIndex]) {
            selectValue(filteredOptions[activeIndex].value);
          }
          break;
        case "Escape":
          e.preventDefault();
          // Only this list closes: a dialog around the Select listens for
          // Escape on the document and would otherwise close on the same key.
          // The App Router roots React AT the document, so stopPropagation
          // cannot stop a listener on that same node; stopImmediatePropagation
          // can, since React's listener was added first (at hydration).
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setOpen(false);
          setActiveIndex(-1);
          triggerRef.current?.focus();
          break;
        case "Tab":
          setOpen(false);
          setActiveIndex(-1);
          break;
        default:
          // First-letter typeahead — skipped when `searchable` because the
          // search input handles typing directly.
          if (!searchable && e.key.length === 1) {
            e.preventDefault();
            const search = typeAhead + e.key.toLowerCase();
            setTypeAhead(search);
            clearTimeout(typeAheadTimer.current);
            typeAheadTimer.current = setTimeout(() => setTypeAhead(""), 500);

            const match = options.findIndex((o) =>
              o.label.toLowerCase().startsWith(search),
            );
            if (match >= 0) setActiveIndex(match);
          }
      }
    },
    [open, disabled, activeIndex, filteredOptions, options, openDropdown, selectValue, typeAhead, searchable],
  );

  // ── Styles ───────────────────────────────────────────────────────────────

  const isDark = variant === "dark";

  const triggerCls = triggerClassName ?? [
    "flex w-full items-center justify-between gap-8 text-sm transition-colors",
    isDark
      ? [
          "border-0 border-b bg-transparent px-0 py-13",
          error
            ? "border-error-line text-[#FCA5A5]"
            : "border-white/30 text-white/70 hover:border-white/50 focus-visible:border-gold",
        ].join(" ")
      : [
          "rounded-control border bg-white px-13 py-13",
          error
            ? "border-error-line bg-red-50/40 text-navy"
            : "border-navy/20 text-navy hover:border-navy/40 focus-visible:border-gold",
        ].join(" "),
    "cursor-pointer focus-visible:outline-none",
    disabled ? "opacity-60 cursor-not-allowed" : "",
  ].join(" ");

  // Dropdown rendered with `position: fixed` (portalled to body), so the
  // positioning utilities are inline styles — Tailwind `top-full` etc. would
  // only work for an absolute child of the trigger's container.
  const dropdownCls = dropdownClassName ?? [
    `fixed ${inModal ? "z-overlay-menu" : "z-dropdown"} w-max max-w-[min(400px,90vw)] max-h-[240px] overflow-y-auto scrollbar-thin rounded-pop border`,
    isDark
      ? "border-white/15 bg-navy shadow-e4-inverse"
      : "border-card-line bg-white shadow-e4",
  ].join(" ");

  // Anchor to whichever horizontal edge keeps a content-driven (and thus
  // potentially wider-than-trigger) dropdown inside the viewport. The default
  // is left-anchored; when the trigger sits in the right part of the viewport
  // (e.g. the right-aligned /search Sort control) a left-anchored panel would
  // grow off the right edge and clip its longest option — most visible in
  // locales with longer labels (VI). In that case anchor the panel's right
  // edge to the trigger's right edge so it grows inward instead. `maxWidth`
  // is a final clamp so the panel can never exceed the viewport on either side.
  const anchorRight =
    typeof window !== "undefined" &&
    triggerRect != null &&
    triggerRect.right > window.innerWidth * 0.6;

  const dropdownStyle: React.CSSProperties | undefined = triggerRect
    ? {
        ...(anchorRight
          ? { right: Math.max(8, window.innerWidth - triggerRect.right) }
          : { left: triggerRect.left }),
        // 4px gap between trigger and dropdown
        top: flipUp ? "auto" : triggerRect.bottom + 4,
        bottom: flipUp ? `calc(100vh - ${triggerRect.top}px + 4px)` : "auto",
        minWidth: triggerRect.width,
        maxWidth: "calc(100vw - 16px)",
      }
    : undefined;

  // Shared option-row renderer — used by both the plain `<ul>` dropdown
  // and the searchable variant (which wraps the same `<ul>` under a search
  // input). Defined inline so it closes over `selectValue` / `currentValue`
  // / `activeIndex` / `isDark` without prop-drilling.
  const renderOption = (option: SelectOption, i: number) => {
    const isSelected = option.value === currentValue;
    const isActive = i === activeIndex;
    return (
      <li
        key={option.value}
        id={`${uid}-opt-${i}`}
        role="option"
        aria-selected={isSelected}
        className={[
          "flex cursor-pointer items-center justify-between gap-13 px-13 py-13 text-sm transition-colors",
          isDark
            ? isActive
              ? "bg-white/10 text-white outline outline-1 outline-white/30 -outline-offset-1"
              : "text-white/80 hover:bg-white/5"
            : isActive
              ? "bg-navy/5 text-navy outline outline-1 outline-navy/20 -outline-offset-1"
              : "text-gray-600 hover:bg-surface",
        ].join(" ")}
        onMouseDown={(e) => {
          e.preventDefault();
          selectValue(option.value);
        }}
        onMouseEnter={() => setActiveIndex(i)}
      >
        {option.label}
        {isSelected && (
          <Check
            className={[
              "h-[14px] w-[14px] shrink-0",
              isDark ? "text-gold" : "text-navy",
            ].join(" ")}
            weight="bold"
            aria-hidden="true"
          />
        )}
      </li>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Hidden input for native form submission */}
      {name && <input type="hidden" name={name} value={currentValue} />}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeOption ? `${uid}-opt-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        aria-required={required}
        aria-invalid={error}
        disabled={disabled}
        className={triggerCls}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        onKeyDown={handleKeyDown}
      >
        {triggerIcon}
        <span className={!selectedOption ? (isDark ? "text-white/50" : "text-gray-500") : ""}>
          {selectedOption?.label ?? placeholder}
        </span>
        <CaretDown
          className={[
            "h-[14px] w-[14px] shrink-0 transition-transform duration-150",
            open ? "rotate-180" : "",
            isDark ? "text-white/50" : "text-navy/40",
          ].join(" ")}
          weight="bold"
          aria-hidden="true"
        />
      </button>

      {/* Dropdown — portalled to body so it escapes ancestor `overflow:
          hidden` clipping (e.g. the apply form's internal scroll container).
          Position is anchored to the trigger via JS-computed coords. When
          `searchable`, a search input sits above the listbox (outside the
          <ul> for semantics — listbox children are role="option"). */}
      {open && triggerRect && typeof document !== "undefined" && createPortal(
        searchable ? (
          <div
            ref={dropdownRef as React.RefObject<HTMLDivElement>}
            className={[
              dropdownCls,
              // Override max-h: with the search bar we want more list room.
              "!max-h-[320px] flex flex-col",
            ].join(" ")}
            style={dropdownStyle}
          >
            <div
              className={[
                "relative shrink-0 border-b",
                isDark ? "border-white/15" : "border-navy/10",
              ].join(" ")}
            >
              <MagnifyingGlass
                aria-hidden="true"
                weight="bold"
                className={[
                  "pointer-events-none absolute left-13 top-1/2 h-[13px] w-[13px] -translate-y-1/2",
                  isDark ? "text-white/40" : "text-gray-500",
                ].join(" ")}
              />
              <input
                ref={searchInputRef}
                type="search"
                inputMode="search"
                autoComplete="off"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  // After the user starts typing, snap focus to the top of
                  // the filtered list — Enter then picks the top match.
                  setActiveIndex(0);
                }}
                onKeyDown={(e) => {
                  // The search input owns text input; navigation / commit /
                  // dismiss keys forward to the shared listbox handler so
                  // Arrows move the active option, Enter selects, Escape
                  // closes, and Tab closes (without preventing the default
                  // tab-focus move).
                  if (["ArrowDown", "ArrowUp", "Enter", "Escape", "Home", "End", "Tab"].includes(e.key)) {
                    handleKeyDown(e);
                  }
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listboxId}
                className={[
                  "w-full bg-transparent py-13 pl-34 pr-34 text-sm focus:outline-none",
                  isDark ? "text-white placeholder:text-white/40" : "text-navy placeholder:text-gray-500",
                ].join(" ")}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    searchInputRef.current?.focus();
                  }}
                  aria-label={t("clearSearch")}
                  className={[
                    "absolute right-8 top-1/2 flex h-[20px] w-[20px] -translate-y-1/2 items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold",
                    isDark ? "text-white/50 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-navy/5 hover:text-navy",
                  ].join(" ")}
                >
                  <XIcon aria-hidden="true" weight="bold" className="h-[11px] w-[11px]" />
                </button>
              )}
            </div>
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel ?? placeholder}
              className="flex-1 overflow-y-auto scrollbar-thin"
            >
              {filteredOptions.length === 0 ? (
                <li
                  className={[
                    "px-13 py-13 text-sm italic",
                    isDark ? "text-white/40" : "text-gray-500",
                  ].join(" ")}
                >
                  No matches.
                </li>
              ) : (
                filteredOptions.map((option, i) => renderOption(option, i))
              )}
            </ul>
          </div>
        ) : (
          <ul
            ref={(el) => {
              listRef.current = el;
              dropdownRef.current = el;
            }}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel ?? placeholder}
            className={dropdownCls}
            style={dropdownStyle}
          >
            {filteredOptions.map((option, i) => renderOption(option, i))}
          </ul>
        ),
        document.body,
      )}
    </div>
  );
}
